import { Link, useNavigate, useParams, } from 'react-router-dom';
import { Formik, Form, Field, } from 'formik';
import * as yup from 'yup';
import { useFirstMountState, } from 'react-use';
import { useSnackbar, } from 'notistack';
import {
    Typography,
    TextField,
    Button,
    Stack,
    Alert,
    Tooltip,
    IconButton, } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth, useFirestore, useDocument, } from '../hooks';
import SimpleBackdrop from '../components/simple-backdrop';

const validationSchema = yup.object({
    title: yup
        .string()
        .min(1, 'Название папки должно состоять минимум из 1 символа')
        .max(50, 'Название папки может состоять не более чем из 50 символов')
        .required('Название папки обязательно к заполнению'),
    description: yup
        .string()
        .max(500, 'Описание папки может состоять не более чем из 500 символов'),
});

const EditFolderPage = () => {
    const { id, } = useParams();
    const { user, } = useAuth();
    const { document, isPending: isPendingDocument, error: errorDocument, } = useDocument({
        col: 'folders',
        id,
        uid: user.uid,
    });
    const { updateDocument, deleteFieldDocument, response: { isPending: isPendingFirestore, }, } = useFirestore('folders');

    const { enqueueSnackbar, } = useSnackbar();
    const isFirstMount = useFirstMountState();
    const navigate = useNavigate();

    const onSubmit = async ({ title, description, }) => {
        const patch = {
            title,
        };

        if (description) {
            patch.description = description;
        } else if (document.description && !(description.trim().length)) {
            await deleteFieldDocument(document.id, 'description');
        }

        const res = await updateDocument(document.id, patch);
        enqueueSnackbar('Изменения сохранены', { variant: 'success', });

        res && navigate(`/folder/${id}`);
    };

    return (
        isFirstMount
            ? null
            : isPendingDocument
            ? <SimpleBackdrop open={isPendingDocument} />
            : errorDocument
                ? <Alert severity='error' variant='filled'>{errorDocument.message}</Alert>
                : document
                    ? <>
                        <Stack direction='row' alignItems='center' sx={{mb: [3, 4, 5, 6],}}>
                            <Tooltip title='Назад к папке' placement='top' arrow>
                                <IconButton
                                    component={Link}
                                    to={`/folder/${document.id}`}
                                    sx={{
                                        mr: [1, 1.25, 1.5, 1.75],
                                        p: [0.25, 0.3, 0.4, 0.5],
                                        '&:hover': {
                                            borderColor: 'secondary.main',
                                            '& .MuiSvgIcon-root': {
                                                color: 'secondary.main',
                                            },
                                        },
                                    }}>
                                    <ArrowBackIcon sx={{fontSize: [20, 22, 24, 26],}} />
                                </IconButton>
                            </Tooltip>
                            <Typography
                                component='h2'
                                sx={{
                                    fontSize: [18, 21, 24, 27],
                                    fontWeight: 700, }}>Редактирование папки</Typography>
                        </Stack>
                        <Formik
                            initialValues={
                                {
                                    title: document.title,
                                    description: document.description || '',
                                }
                            }
                            validationSchema={validationSchema}
                            onSubmit={onSubmit}
                            validateOnChange={false}
                            validateOnBlur={false}
                        >
                            {
                                (formik) => {
                                    return (
                                        <Form autoComplete='off'>
                                            <Field name='title'>
                                                {
                                                    (props) => {
                                                        const { field, meta: { error, touched, }, } = props;

                                                        return <TextField
                                                            {...field}
                                                            color='secondary'
                                                            fullWidth
                                                            label='Название папки'
                                                            error={touched && Boolean(error)}
                                                            helperText={touched && error}
                                                            sx={{mb: [2, 2, 3],}}
                                                        />
                                                    }
                                                }
                                            </Field>
                                            <Field name='description'>
                                                {
                                                    (props) => {
                                                        const { field, meta: { error, touched, }, } = props;

                                                        return <TextField
                                                            {...field}
                                                            color='secondary'
                                                            fullWidth
                                                            label='Краткое описание (не обязательно)'
                                                            error={touched && Boolean(error)}
                                                            helperText={touched && error}
                                                            sx={{mb: 5,}}
                                                        />
                                                    }
                                                }
                                            </Field>
                                            <Stack direction='row' justifyContent='flex-end'>
                                                <Button
                                                    type='submit'
                                                    disabled={isPendingFirestore || !formik.dirty}
                                                    onClick={() => {
                                                        formik.validateForm();
                                                    }}
                                                    sx={{
                                                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'common.rhino' : 'common.kashmirBlue',
                                                        boxShadow: 2,
                                                        color: 'common.white',
                                                        py: 1,
                                                        px: 1.5,
                                                        '&:hover': {
                                                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'common.rhino' : 'common.kashmirBlue',
                                                        },
                                                        '&:disabled': {
                                                            color: 'rgba(255, 255, 255, 0.26)',
                                                        },
                                                    }}>{ isPendingFirestore ? 'Сохраняем...' : 'Готово' }</Button>
                                            </Stack>
                                        </Form>
                                    )
                                }
                            }
                        </Formik>
                    </>
                    : <Typography sx={{fontSize: 14,}}>Запрашиваемые данные отсутствуют</Typography>
    )
};

export default EditFolderPage;